import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CopyToClipboard,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";

import { setDynamicData } from "./api";

/** Shown so a stalled pipeline is at least visible to someone who looks. */
export const formatRefresh = (solution) => {
  if (!solution || !solution.lastRefreshAt) return "never";
  const when = solution.lastRefreshAt.replace("T", " ").replace(/:\d\dZ$/, "");
  return solution.lastRefreshBy ? `${when} by ${solution.lastRefreshBy}` : when;
};

/**
 * Refreshing data, for one UI app.
 *
 * Switching this on does not make data appear, and it is not self-service
 * today: it records the intent, raises a ticket to the BI team to get a
 * pipeline built, and hands over the identifiers that pipeline needs.
 *
 * That ticket step is the interim stage. Once the ATEAM SDK ships an app will
 * fetch its own data and this screen loses its reason to exist.
 */
const DynamicDataModal = ({ visible, onDismiss, solution, onChanged }) => {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (visible && solution) {
      setEnabled(Boolean(solution.dynamicDataEnabled));
      setResult(null);
      setError(null);
    }
  }, [visible, solution]);

  if (!solution) return null;

  const handleToggle = async (checked) => {
    setEnabled(checked);
    setSaving(true);
    setError(null);
    try {
      const response = await setDynamicData(solution.id, checked);
      setResult(response);
      if (onChanged) onChanged(response);
    } catch (caught) {
      setEnabled(!checked);
      setError(caught && caught.message ? caught.message : "Could not save that. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const ticket = result && result.biTicket;
  const lambdaName = (result && result.refreshLambdaName) || null;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={<Header variant="h2">{solution.name} — Refreshing data</Header>}
      footer={
        <Box float="right">
          <Button onClick={onDismiss}>Close</Button>
        </Box>
      }
      data-testid="byoc-dynamic-data-modal"
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}

        <Checkbox
          checked={enabled}
          disabled={saving}
          onChange={({ detail }) => handleToggle(detail.checked)}
          data-testid="byoc-dynamic-data-toggle"
        >
          This app needs data that refreshes
        </Checkbox>

        {enabled && (
          <>
            <Alert type="info" header="We'll raise a ticket to the BI team">
              Your data comes from a BI pipeline. Switching this on asks the BI team to set one up for
              this app — it does not move any data by itself.
            </Alert>

            {ticket && (
              <KeyValuePairs
                columns={2}
                items={[
                  {
                    label: "Ticket",
                    value: ticket.url && ticket.url !== "#" ? (
                      <Link href={ticket.url} external>
                        {ticket.id}
                      </Link>
                    ) : (
                      ticket.id
                    ),
                  },
                  {
                    label: "Status",
                    value: <StatusIndicator type="in-progress">{ticket.status}</StatusIndicator>,
                  },
                ]}
              />
            )}

            <Box variant="h4">Give these to the BI team for their pipeline</Box>
            <KeyValuePairs
              columns={1}
              items={[
                {
                  label: "code_ref_id",
                  value: (
                    <SpaceBetween direction="horizontal" size="xs">
                      <span>{solution.id}</span>
                      <CopyToClipboard
                        copyButtonAriaLabel="Copy code_ref_id"
                        copyErrorText="Failed to copy"
                        copySuccessText="code_ref_id copied"
                        textToCopy={solution.id}
                        variant="icon"
                      />
                    </SpaceBetween>
                  ),
                },
                {
                  label: "code_name",
                  value: (
                    <SpaceBetween direction="horizontal" size="xs">
                      <span>{solution.name}</span>
                      <CopyToClipboard
                        copyButtonAriaLabel="Copy code_name"
                        copyErrorText="Failed to copy"
                        copySuccessText="code_name copied"
                        textToCopy={solution.name}
                        variant="icon"
                      />
                    </SpaceBetween>
                  ),
                },
                ...(lambdaName
                  ? [
                      {
                        label: "Refresh Lambda",
                        value: (
                          <SpaceBetween direction="horizontal" size="xs">
                            <span>{lambdaName}</span>
                            <CopyToClipboard
                              copyButtonAriaLabel="Copy Lambda name"
                              copyErrorText="Failed to copy"
                              copySuccessText="Lambda name copied"
                              textToCopy={lambdaName}
                              variant="icon"
                            />
                          </SpaceBetween>
                        ),
                      },
                    ]
                  : []),
                { label: "Last refresh", value: formatRefresh(solution) },
              ]}
            />

            <Box variant="small">
              The pipeline calls the refresh Lambda after each dataset run. It replaces the data and
              clears the cache, so the app shows new data on the next load.
            </Box>
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default DynamicDataModal;
